import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SerializedAccountForList } from 'fym-common/src/db/serializersTypes';
import { useGetAccountsQuery } from '../../redux/api/fymAPI';
import { useAccountId, useUserToken } from '../../redux/slices/appInfo/hooks';
import { openSMLUpgradePrompt, setAccountId } from '../../redux/slices/appInfo/slice';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRouteNames, SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useMusic24Capabilities } from '../music24/hooks/useMusic24Capabilities';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { useCurrentNavScreen } from '../music24/navigation/hooks/useCurrentNavScreen';
import { findArtistItemParent } from '../music24/navigation/smlNavigation';
import { useDropAccountIdFromUrl } from './useDropAccountIdFromUrl';
import { countOwnArtists } from './demoAccounts';

export interface ArtistSwitcherState {
  /** `undefined` while the list loads. */
  accounts: SerializedAccountForList[] | undefined;
  /**
   * The user's own artists, without the read-only demo artist. Every count and limit check uses
   * it (design: music24-demo-artist a1). `undefined` while the list loads.
   */
  ownAccountCount: number | undefined;
  active: SerializedAccountForList | undefined;
  activeAccountId: number | null;
  /** `null` means unlimited. */
  artistsLimit: number | null;
  onSelect: (accountId: number) => void;
  isAddOpen: boolean;
  openAdd: () => void;
  closeAdd: () => void;
  onUpgradeHint: () => void;
}

/** The data and the actions every artist switcher shares: the sidebar, the pills and the rail slot. */
export function useArtistSwitcher(): ArtistSwitcherState {
  const dispatch = useDispatch();
  const router = useSMLRouter();
  const { screen } = useCurrentNavScreen();
  const dropAccountIdFromUrl = useDropAccountIdFromUrl();
  const userToken = useUserToken();
  const activeAccountId = useAccountId();
  const { artistsLimit } = useMusic24Capabilities();
  const [isAddOpen, setAddOpen] = useState(false);

  const { accounts } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data }) => ({ accounts: data?.accounts }),
  });
  const ownAccountCount = useMemo(() => countOwnArtists(accounts), [accounts]);
  const active = useMemo(
    () => accounts?.find((account) => account.id === activeAccountId),
    [accounts, activeAccountId]
  );

  const onSelect = useCallback(
    (accountId: number) => {
      // design: navigation i1. The item on a page below a Yours or Promote destination belongs to
      // the old artist, so a switch goes back to the parent list. The new route has no `?accountId=`.
      const isSwitch = accountId !== activeAccountId;
      const parent = findArtistItemParent(screen);
      dispatch(setAccountId({ accountId }));
      // The same artist again on a page below a destination (one smartlink, its editor): that page
      // reads `?accountId=` and throws without it (MUSIC24-WEB-6), so keep the URL as it is.
      if (!isSwitch && parent !== undefined) {
        return;
      }
      // The Studio keeps the old artist's reel (`?reel=`) and song (`?catalogItemUuid=`) in the
      // query, so a switch opens the empty Studio of the new artist.
      if (isSwitch && screen === SMLRouteNames.promoteStudio) {
        router.replace(SMLRoutes.promoteStudio);
        return;
      }
      if (parent !== undefined) {
        router.replace(parent.route);
        return;
      }
      dropAccountIdFromUrl();
    },
    [activeAccountId, dispatch, dropAccountIdFromUrl, router, screen]
  );

  const openAdd = useCallback(() => setAddOpen(true), []);
  const closeAdd = useCallback(() => setAddOpen(false), []);
  const onUpgradeHint = useCallback(() => {
    dispatch(
      openSMLUpgradePrompt({
        feature: SMLUpgradeFeature.artists,
        limit: artistsLimit,
        used: ownAccountCount ?? null,
      })
    );
  }, [dispatch, artistsLimit, ownAccountCount]);

  return {
    accounts,
    ownAccountCount,
    active,
    activeAccountId,
    artistsLimit,
    onSelect,
    isAddOpen,
    openAdd,
    closeAdd,
    onUpgradeHint,
  };
}
